/**
 * LeaderboardPage — Status & Identity for Origin CRM.
 *
 * Company-visible production rankings (funded volume/units from the Arive
 * book), a top-3 podium, President's Club progress bars vs admin-set targets,
 * the $10 Million Club (YTD volume milestone worth extra comp bps), this
 * week's wins, and each LO's private FutureBot monthly coaching review.
 *
 * Admins additionally get:
 *  - a view-as user selector (highlighting, rank badge, and the coaching
 *    review all follow the selected LO)
 *  - an Admin panel managing President's Club targets, $10M Club milestone/
 *    bonus, and account EXCLUSIONS (leadership accounts holding inherited
 *    past-client books never show anywhere on the page).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  FiArrowDown,
  FiArrowUp,
  FiAward,
  FiChevronDown,
  FiChevronUp,
  FiDollarSign,
  FiMinus,
  FiRefreshCw,
  FiSettings,
  FiStar,
  FiTrendingUp,
  FiUserX,
  FiZap,
} from 'react-icons/fi';
import { FaCrown } from 'react-icons/fa';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';

import { app, db } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
import { useLeaderboard, LEADERBOARD_PERIODS, getYtdMonthPeriods } from '../../hooks/useLeaderboard';
import { useAgentPage } from '../../components/FutureBot/FutureBotProvider';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import { useLeaderboardAgentTools } from './agent/useLeaderboardAgentTools';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const fmtVolume = (n) => {
  const num = Number(n) || 0;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(num >= 1e7 ? 1 : 2)}M`;
  if (num >= 1e3) return `$${Math.round(num / 1e3)}K`;
  return `$${Math.round(num)}`;
};

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(`${String(d).slice(0, 10)}T12:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const MEDALS = ['#FFD700', '#C0C0C0', '#CD7F32'];

function Movement({ movement }) {
  if (movement === null || movement === undefined) {
    return <Icon as={FiMinus} color="gray.400" boxSize={3} />;
  }
  if (movement > 0) {
    return (
      <HStack spacing={0.5}>
        <Icon as={FiArrowUp} color="green.400" boxSize={3.5} />
        <Text fontSize="xs" color="green.400">{movement}</Text>
      </HStack>
    );
  }
  if (movement < 0) {
    return (
      <HStack spacing={0.5}>
        <Icon as={FiArrowDown} color="red.400" boxSize={3.5} />
        <Text fontSize="xs" color="red.400">{Math.abs(movement)}</Text>
      </HStack>
    );
  }
  return <Icon as={FiMinus} color="gray.400" boxSize={3} />;
}

// ---------------------------------------------------------------------------
// Sortable rankings config
// ---------------------------------------------------------------------------

const RANKING_COLUMNS = [
  { key: 'rank', label: '#', numeric: false, width: '72px' },
  { key: 'name', label: 'Loan Officer', numeric: false },
  { key: 'fundedVolume', label: 'Funded', numeric: true },
  { key: 'fundedUnits', label: 'Units', numeric: true },
  { key: 'totalLeads', label: 'Leads', numeric: true },
  { key: 'conversionPct', label: 'Conv %', numeric: true, tooltip: 'Funded deals / leads created in this window' },
  { key: 'avgStlBiz', label: 'STL', numeric: true, tooltip: 'Avg speed-to-lead in business minutes (Monday Report)' },
  { key: 'conversationPct', label: 'Conv. Rate', numeric: true, tooltip: '% of new leads reached in a 2-way conversation' },
];

/** Sort rows by a column key; null/undefined values always sink to the bottom. */
function sortRankings(rows, sortKey, sortDir) {
  const dir = sortDir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    const aNull = av === null || av === undefined || av === '';
    const bNull = bv === null || bv === undefined || bv === '';
    if (aNull && bNull) return (a.rank || 0) - (b.rank || 0);
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av).localeCompare(String(bv)) * dir;
    }
    if (av === bv) return (a.rank || 0) - (b.rank || 0);
    return (av - bv) * dir;
  });
}

// ---------------------------------------------------------------------------
// Winner podium
// ---------------------------------------------------------------------------

const PODIUM_STYLES = {
  1: {
    gradient: 'linear(to-b, #FFE566, #FFD700 35%, #B8860B)',
    ring: '#FFD700',
    height: { base: '96px', md: '132px' },
    avatarSize: { base: 'xl', md: '2xl' },
    label: '1st',
  },
  2: {
    gradient: 'linear(to-b, #F2F2F2, #C0C0C0 40%, #8E8E8E)',
    ring: '#C0C0C0',
    height: { base: '72px', md: '96px' },
    avatarSize: { base: 'lg', md: 'xl' },
    label: '2nd',
  },
  3: {
    gradient: 'linear(to-b, #E8A87C, #CD7F32 40%, #8B5A2B)',
    ring: '#CD7F32',
    height: { base: '56px', md: '72px' },
    avatarSize: { base: 'lg', md: 'xl' },
    label: '3rd',
  },
};

function PodiumSpot({ row, place, isMe, meLabel = 'You', subtleText }) {
  const style = PODIUM_STYLES[place];
  if (!row) return null;
  return (
    <VStack spacing={0} flex="1" maxW="260px" justify="flex-end" alignSelf="stretch">
      <VStack spacing={1} mb={3}>
        {place === 1 && <Icon as={FaCrown} color="#FFD700" boxSize={{ base: 5, md: 7 }} mb={-0.5} />}
        <Avatar
          name={row.name}
          src={row.photo || undefined}
          size={style.avatarSize}
          border="3px solid"
          borderColor={style.ring}
          boxShadow={`0 0 0 3px ${style.ring}40`}
        />
        <Text fontWeight="800" fontSize={{ base: 'sm', md: place === 1 ? 'lg' : 'md' }} textAlign="center" noOfLines={1} mt={1.5}>
          {row.name}
        </Text>
        <Text fontWeight="800" fontSize={{ base: 'md', md: place === 1 ? '2xl' : 'xl' }} lineHeight="1">
          {fmtVolume(row.fundedVolume)}
        </Text>
        <Text fontSize="xs" color={subtleText}>
          {row.fundedUnits} unit{row.fundedUnits === 1 ? '' : 's'} funded
        </Text>
        {isMe && <Badge colorScheme="blue">{meLabel}</Badge>}
      </VStack>
      <Box
        w="full"
        h={style.height}
        bgGradient={style.gradient}
        borderTopRadius="lg"
        position="relative"
        boxShadow="inset 0 2px 6px rgba(255,255,255,0.45), inset 0 -6px 12px rgba(0,0,0,0.25)"
      >
        <VStack spacing={0} position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
          <Text fontSize={{ base: '2xl', md: place === 1 ? '4xl' : '3xl' }} fontWeight="900" color="whiteAlpha.900"
            textShadow="0 2px 4px rgba(0,0,0,0.35)" lineHeight="1">
            {place}
          </Text>
          <Text fontSize="2xs" fontWeight="700" color="whiteAlpha.800" letterSpacing="widest" textTransform="uppercase">
            {style.label}
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LeaderboardPage() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const lb = useLeaderboard();
  const {
    period, setPeriod, data, loading, error, refresh,
    review, reviewPeriod, reviewLoading, reviewError, loadReview,
  } = lb;

  const uid = currentUser?.uid;
  const isAdmin = SuperAdmin || Roles?.includes('CRM Admin') || currentUser?.CRMRole === 'CRM Admin';

  // ---- Admin view-as (same selector as Dashboard / My Deals) ----
  const [selectedUserId, setSelectedUserId] = useState('');
  const impersonating = isAdmin && !!selectedUserId && selectedUserId !== uid;
  const viewAsUid = impersonating ? selectedUserId : uid;

  const [crmUsers, setCrmUsers] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    const loadCrmUsers = async () => {
      try {
        const usersQuery = query(collection(db, 'users'), where('CRMUser', '==', true));
        const snapshot = await getDocs(usersQuery);
        const users = snapshot.docs.map((d) => ({
          uid: d.id,
          name: d.data().Name || d.data().name || d.data().email,
          ...d.data(),
        }));
        setCrmUsers(sortCRMUsersWithDTCPriority(users));
      } catch (err) {
        console.error('Leaderboard: CRM users load failed', err);
      }
    };
    loadCrmUsers();
  }, [isAdmin]);

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const tableHeadBg = useColorModeValue('gray.50', 'gray.900');
  const meBg = useColorModeValue('blue.50', 'blue.900');
  const winChipBg = useColorModeValue('green.50', 'green.900');
  const reviewBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.15)');
  const reviewBorder = useColorModeValue('purple.200', 'purple.600');

  // Rankings sort — default is server rank order.
  const [sortKey, setSortKey] = useState('rank');
  const [sortDir, setSortDir] = useState('asc');

  const rankings = useMemo(() => data?.rankings || [], [data]);
  const top3 = rankings.slice(0, 3);
  const club = data?.presidentsClub;
  const club10 = data?.tenMillionClub;
  const wins = data?.weeklyWins || [];

  // "$10M" (or whatever the admin set) for headings and copy.
  const club10Label = useMemo(() => {
    const t = Number(club10?.volumeTarget) || 10000000;
    const mm = t / 1e6;
    return `$${Number.isInteger(mm) ? mm : mm.toFixed(1)}M`;
  }, [club10]);

  const viewAsName = impersonating
    ? (crmUsers.find((u) => u.uid === selectedUserId)?.name
      || rankings.find((r) => r.userId === selectedUserId)?.name
      || 'User')
    : null;
  const viewAsFirst = viewAsName ? viewAsName.split(' ')[0] : null;
  const meLabel = impersonating ? 'Viewing' : 'You';

  // "Me" is whoever the page is viewed as (self unless an admin picked an LO).
  const me = useMemo(
    () => rankings.find((r) => r.userId === viewAsUid) || null,
    [rankings, viewAsUid]
  );

  // The coaching review follows the viewed user; the page owns load timing.
  const loadReviewAs = useCallback(
    (opts = {}) => loadReview({ ...opts, userId: impersonating ? viewAsUid : undefined }),
    [loadReview, impersonating, viewAsUid]
  );
  useEffect(() => { loadReviewAs(); }, [loadReviewAs]);

  // ---- Admin panel: milestone targets + exclusions (config/crmSettings) ----
  const adminModal = useDisclosure();
  const excludedUids = useMemo(() => data?.excludedUids || [], [data]);
  const [exclusionDraft, setExclusionDraft] = useState([]);
  const [milestoneDraft, setMilestoneDraft] = useState({
    pcVolume: 15000000, pcUnits: 50, tmVolume: 10000000, tmBps: 10,
  });
  const [savingAdmin, setSavingAdmin] = useState(false);

  const resolveUserName = useCallback(
    (userId) =>
      crmUsers.find((u) => u.uid === userId)?.name
      || rankings.find((r) => r.userId === userId)?.name
      || userId,
    [crmUsers, rankings]
  );

  const openAdmin = useCallback(() => {
    setExclusionDraft(excludedUids);
    setMilestoneDraft({
      pcVolume: Number(club?.targets?.volumeTarget) || 15000000,
      pcUnits: Number(club?.targets?.unitsTarget) || 50,
      tmVolume: Number(club10?.volumeTarget) || 10000000,
      tmBps: Number(club10?.bonusBps) || 10,
    });
    adminModal.onOpen();
  }, [excludedUids, club, club10, adminModal]);

  const setMilestone = useCallback((key, value) => {
    const num = Number(value);
    setMilestoneDraft((prev) => ({ ...prev, [key]: Number.isFinite(num) ? num : prev[key] }));
  }, []);

  const saveAdminSettings = useCallback(async () => {
    const { pcVolume, pcUnits, tmVolume, tmBps } = milestoneDraft;
    if (!(pcVolume > 0) || !(pcUnits > 0) || !(tmVolume > 0) || !(tmBps > 0)) {
      toast({ title: 'All milestone values must be greater than zero', status: 'warning', duration: 4000 });
      return;
    }
    setSavingAdmin(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');
      await updateCrmSettings({
        updates: {
          presidentsClub: { volumeTarget: pcVolume, unitsTarget: pcUnits },
          tenMillionClub: { volumeTarget: tmVolume, bonusBps: tmBps },
          leaderboard: { excludedUids: exclusionDraft },
        },
      });
      toast({ title: 'Leaderboard settings saved', status: 'success', duration: 4000, isClosable: true });
      adminModal.onClose();
      refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSavingAdmin(false);
    }
  }, [milestoneDraft, exclusionDraft, adminModal, refresh, toast]);

  // FutureBot registration (hooks rule: before any early return). The agent
  // sees the wrapped review loader so its coaching tool follows view-as too.
  const lbForAgent = useMemo(() => ({ ...lb, loadReview: loadReviewAs }), [lb, loadReviewAs]);
  const { registration: agentRegistration } = useLeaderboardAgentTools({
    lb: lbForAgent, uid, viewAsUid, viewAsName,
  });
  useAgentPage(agentRegistration);

  // FeatureTour: named actions the Leaderboard tours can invoke. openAdmin
  // (not adminModal.onOpen) so the drafts are seeded exactly like a real click.
  useTourActions(useMemo(() => ({
    openLeaderboardAdmin: openAdmin,
  }), [openAdmin]));

  const sortedRankings = useMemo(
    () => sortRankings(rankings, sortKey, sortDir),
    [rankings, sortKey, sortDir]
  );

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Metrics start high-to-low; rank and name start low-to-high.
      setSortDir(key === 'rank' || key === 'name' ? 'asc' : 'desc');
    }
  };

  // Completed months of the current year — selectable so the board can show
  // the exact window a coaching review graded.
  const monthPeriods = useMemo(() => getYtdMonthPeriods(), []);
  const isMonthPeriod = /^\d{4}-\d{2}$/.test(period);

  const periodLabel = useMemo(() => {
    const base = LEADERBOARD_PERIODS.find((p) => p.value === period);
    if (base) return base.label;
    const month = monthPeriods.find((p) => p.value === period);
    if (month) return `${month.label} ${month.year}`;
    return period;
  }, [period, monthPeriods]);

  const reviewMonthLabel = useMemo(() => {
    if (!reviewPeriod) return '';
    const [y, m] = reviewPeriod.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [reviewPeriod]);

  // Jump the rankings to the review's month: the matching YTD month option
  // when it exists, otherwise last_month (covers December viewed in January).
  const showReviewMonthOnBoard = useCallback(() => {
    if (!reviewPeriod) return;
    const match = monthPeriods.find((p) => p.value === reviewPeriod);
    setPeriod(match ? match.value : 'last_month');
  }, [reviewPeriod, monthPeriods, setPeriod]);

  // True when the board already shows the review's window (the month option,
  // or "Last Month" — reviews always grade the month that just ended).
  const reviewIsOnBoard = useMemo(() => {
    if (!reviewPeriod) return true;
    if (period === reviewPeriod) return true;
    if (period === 'last_month') {
      const lastYtd = monthPeriods[monthPeriods.length - 1];
      return lastYtd ? lastYtd.value === reviewPeriod : true;
    }
    return false;
  }, [period, reviewPeriod, monthPeriods]);

  return (
    <Box bg={pageBg} minH="100vh" pb={12}>
      <Container maxW="1400px" pt={6}>
        {/* Header */}
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4} mb={6}>
          <Box data-tour="lb-header">
            <HStack spacing={3}>
              <Icon as={FiAward} boxSize={7} color="yellow.400" />
              <Heading size="lg">Leaderboard</Heading>
            </HStack>
            <Text color={subtleText} mt={1}>
              Company production — {isMonthPeriod ? periodLabel : periodLabel.toLowerCase()}. Funded volume comes straight from the Arive book.
            </Text>
          </Box>
          <HStack spacing={3} flexWrap="wrap">
            {isAdmin && (
              <Select
                size="sm"
                maxW="220px"
                borderRadius="md"
                placeholder="Viewing as myself"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                data-tour="lb-view-as"
              >
                {(() => {
                  const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                  return (
                    <>
                      {dtcUsers.length > 0 && (
                        <optgroup label="DTC Loan Officers">
                          {dtcUsers.map((user) => (
                            <option key={user.uid} value={user.uid}>{user.name}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                        {otherUsers.map((user) => (
                          <option key={user.uid} value={user.uid}>{user.name}</option>
                        ))}
                      </optgroup>
                    </>
                  );
                })()}
              </Select>
            )}
            <Select value={period} onChange={(e) => setPeriod(e.target.value)} size="sm" maxW="180px" borderRadius="md" data-tour="lb-period">
              {LEADERBOARD_PERIODS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
              {monthPeriods.length > 0 && (
                <optgroup label={`Months — ${monthPeriods[0].year}`}>
                  {monthPeriods.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </optgroup>
              )}
            </Select>
            <Tooltip label="Refresh">
              <IconButton aria-label="Refresh" icon={<FiRefreshCw />} size="sm" variant="outline" isLoading={loading} onClick={() => refresh()} />
            </Tooltip>
            {isAdmin && (
              <Button size="sm" leftIcon={<FiSettings />} variant="outline" onClick={openAdmin} data-tour="lb-admin-btn">
                Admin
              </Button>
            )}
          </HStack>
        </Flex>

        {impersonating && (
          <Alert status="info" borderRadius="md" mb={4} py={2}>
            <AlertIcon />
            <Text fontSize="sm">
              Viewing the leaderboard as <strong>{viewAsName}</strong> — highlighting, the rank badge,
              and the monthly review below are theirs.
            </Text>
          </Alert>
        )}

        {error && (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Company totals */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6} data-tour="lb-totals">
          <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4}>
            <Stat>
              <StatLabel>Company Funded Volume</StatLabel>
              <Skeleton isLoaded={!loading} borderRadius="md">
                <StatNumber>{fmtVolume(data?.totals?.fundedVolume)}</StatNumber>
              </Skeleton>
            </Stat>
          </Box>
          <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4}>
            <Stat>
              <StatLabel>Units Funded</StatLabel>
              <Skeleton isLoaded={!loading} borderRadius="md">
                <StatNumber>{data?.totals?.fundedUnits ?? 0}</StatNumber>
              </Skeleton>
            </Stat>
          </Box>
          <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4}>
            <Stat>
              <StatLabel>LOs on the Board</StatLabel>
              <Skeleton isLoaded={!loading} borderRadius="md">
                <StatNumber>{data?.totals?.participants ?? 0}</StatNumber>
              </Skeleton>
            </Stat>
          </Box>
        </SimpleGrid>

        {/* Winner podium: 2nd — 1st — 3rd, pedestal heights by place */}
        {top3.length > 0 && (
          <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="xl" pt={6} px={{ base: 3, md: 8 }} mb={6} overflow="hidden" data-tour="lb-podium">
            <Flex align="flex-end" justify="center" gap={{ base: 2, md: 6 }}>
              <PodiumSpot row={top3[1]} place={2} isMe={top3[1]?.userId === viewAsUid} meLabel={meLabel} subtleText={subtleText} />
              <PodiumSpot row={top3[0]} place={1} isMe={top3[0]?.userId === viewAsUid} meLabel={meLabel} subtleText={subtleText} />
              <PodiumSpot row={top3[2]} place={3} isMe={top3[2]?.userId === viewAsUid} meLabel={meLabel} subtleText={subtleText} />
            </Flex>
          </Box>
        )}

        {/* This week's wins */}
        {wins.length > 0 && (
          <Box mb={6}>
            <HStack mb={2} spacing={2}>
              <Icon as={FiZap} color="green.400" />
              <Heading size="sm">This Week&apos;s Wins</Heading>
            </HStack>
            <Flex gap={2} overflowX="auto" pb={1}>
              {wins.map((w, i) => (
                <Box key={i} bg={winChipBg} borderRadius="full" px={4} py={1.5} whiteSpace="nowrap" flexShrink={0}>
                  <Text fontSize="sm">
                    <strong>{w.loName || 'Team'}</strong> funded {fmtVolume(w.volume)}
                    {w.borrower ? ` for ${w.borrower}` : ''} · {fmtDate(w.fundedDate)}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="flex-start">
          {/* Rankings table */}
          <Box gridColumn={{ xl: 'span 2' }} bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" overflow="hidden" data-tour="lb-rankings">
            <Flex px={4} py={3} justify="space-between" align="center">
              <Heading size="sm">Rankings — {periodLabel}</Heading>
              {me && (
                <Badge colorScheme="blue" fontSize="0.75rem">
                  {impersonating ? `${viewAsFirst} is` : 'You\u2019re'} #{me.rank} · {fmtVolume(me.fundedVolume)}
                </Badge>
              )}
            </Flex>
            {loading && !data ? (
              <Flex justify="center" py={16}><Spinner size="lg" /></Flex>
            ) : rankings.length === 0 ? (
              <VStack py={12}>
                <Text color={subtleText}>No production found for this period.</Text>
              </VStack>
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead bg={tableHeadBg}>
                    <Tr>
                      {RANKING_COLUMNS.map((col) => {
                        const active = sortKey === col.key;
                        const header = (
                          <HStack spacing={0.5} display="inline-flex" justify={col.numeric ? 'flex-end' : 'flex-start'}>
                            <Text as="span">{col.label}</Text>
                            {active && <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} boxSize={3.5} />}
                          </HStack>
                        );
                        return (
                          <Th
                            key={col.key}
                            isNumeric={col.numeric}
                            w={col.width}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleSort(col.key)}
                            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                            _hover={{ color: 'blue.400' }}
                          >
                            {col.tooltip ? <Tooltip label={col.tooltip}>{header}</Tooltip> : header}
                          </Th>
                        );
                      })}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedRankings.map((row) => {
                      const isMe = row.userId === viewAsUid;
                      return (
                        <Tr key={row.userId} bg={isMe ? meBg : undefined} fontWeight={isMe ? '600' : 'normal'}>
                          <Td>
                            <HStack spacing={1.5}>
                              <Text>{row.rank}</Text>
                              <Movement movement={row.movement} />
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Avatar name={row.name} src={row.photo || undefined} size="xs" />
                              <Text>{row.name}{isMe ? (impersonating ? ' (viewing)' : ' (you)') : ''}</Text>
                              {row.rank <= 3 && <Icon as={FiAward} color={MEDALS[row.rank - 1]} boxSize={3.5} />}
                            </HStack>
                          </Td>
                          <Td isNumeric fontWeight="600">{fmtVolume(row.fundedVolume)}</Td>
                          <Td isNumeric>{row.fundedUnits}</Td>
                          <Td isNumeric>{row.totalLeads}</Td>
                          <Td isNumeric>{row.conversionPct !== null && row.conversionPct !== undefined ? `${row.conversionPct}%` : '—'}</Td>
                          <Td isNumeric>{row.avgStlBiz !== null && row.avgStlBiz !== undefined ? `${row.avgStlBiz}m` : '—'}</Td>
                          <Td isNumeric>{row.conversationPct !== null && row.conversationPct !== undefined ? `${row.conversationPct}%` : '—'}</Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>

          {/* Right rail: President's Club + coaching review */}
          <VStack spacing={6} align="stretch">
            {/* President's Club */}
            <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4} data-tour="lb-pc-card">
              <HStack justify="space-between" mb={1}>
                <HStack spacing={2}>
                  <Icon as={FiStar} color="yellow.400" />
                  <Heading size="sm">President&apos;s Club {club?.year || ''}</Heading>
                </HStack>
                {club && (
                  <Tooltip label={`${club.yearElapsedPct}% of the year has elapsed`}>
                    <Badge variant="subtle">{club.yearElapsedPct}% of year</Badge>
                  </Tooltip>
                )}
              </HStack>
              <Text fontSize="xs" color={subtleText} mb={3}>
                Qualify on {fmtVolume(club?.targets?.volumeTarget)} volume or {club?.targets?.unitsTarget ?? '—'} units (YTD).
              </Text>
              {loading && !data ? (
                <Skeleton h="120px" borderRadius="md" />
              ) : (club?.progress || []).length === 0 ? (
                <Text fontSize="sm" color={subtleText}>No YTD production yet.</Text>
              ) : (
                <VStack spacing={3} align="stretch">
                  {(club.progress || []).slice(0, 10).map((p) => {
                    const isMe = p.userId === viewAsUid;
                    const pct = Math.max(p.volumePct, p.unitsPct);
                    return (
                      <Box key={p.userId} bg={isMe ? meBg : undefined} borderRadius="md" p={isMe ? 2 : 0}>
                        <Flex justify="space-between" align="center" mb={1}>
                          <HStack spacing={1.5}>
                            <Text fontSize="sm" fontWeight={isMe ? '700' : '500'}>{p.name}{isMe ? (impersonating ? ' (viewing)' : ' (you)') : ''}</Text>
                            {p.qualified && <Badge colorScheme="yellow" fontSize="0.6rem">QUALIFIED</Badge>}
                            {!p.qualified && p.onPace && <Badge colorScheme="green" fontSize="0.6rem">ON PACE</Badge>}
                          </HStack>
                          <Text fontSize="xs" color={subtleText}>
                            {fmtVolume(p.ytdVolume)} · {p.ytdUnits}u
                          </Text>
                        </Flex>
                        <Progress
                          value={Math.min(pct, 100)}
                          size="sm"
                          borderRadius="full"
                          colorScheme={p.qualified ? 'yellow' : p.onPace ? 'green' : 'blue'}
                        />
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>

            {/* $10 Million Club — YTD volume milestone worth extra comp bps */}
            <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4} data-tour="lb-club-card">
              <HStack justify="space-between" mb={1}>
                <HStack spacing={2}>
                  <Icon as={FiDollarSign} color="green.400" />
                  <Heading size="sm">{club10Label} Club {club10?.year || ''}</Heading>
                </HStack>
                {club10 && (
                  <Badge colorScheme="green" variant="subtle">+{club10.bonusBps} bps</Badge>
                )}
              </HStack>
              <Text fontSize="xs" color={subtleText} mb={3}>
                Fund {fmtVolume(club10?.volumeTarget)} YTD and earn an additional {club10?.bonusBps ?? 10} bps in comp.
              </Text>
              {loading && !data ? (
                <Skeleton h="100px" borderRadius="md" />
              ) : (club10?.progress || []).length === 0 ? (
                <Text fontSize="sm" color={subtleText}>No YTD production yet.</Text>
              ) : (
                <VStack spacing={3} align="stretch">
                  {(() => {
                    const members = club10.progress.filter((p) => p.qualified);
                    const chasing = club10.progress.filter((p) => !p.qualified).slice(0, 5);
                    return (
                      <>
                        {members.length > 0 ? (
                          <Box>
                            <Text fontSize="2xs" fontWeight="700" color={subtleText} textTransform="uppercase" letterSpacing="wide" mb={1.5}>
                              In the club
                            </Text>
                            <VStack spacing={1.5} align="stretch">
                              {members.map((p) => {
                                const isMe = p.userId === viewAsUid;
                                return (
                                  <Flex key={p.userId} justify="space-between" align="center"
                                    bg={isMe ? meBg : undefined} borderRadius="md" px={isMe ? 2 : 0} py={isMe ? 1 : 0}>
                                    <HStack spacing={1.5}>
                                      <Icon as={FiAward} color="green.400" boxSize={3.5} />
                                      <Text fontSize="sm" fontWeight={isMe ? '700' : '600'}>
                                        {p.name}{isMe ? (impersonating ? ' (viewing)' : ' (you)') : ''}
                                      </Text>
                                    </HStack>
                                    <Text fontSize="xs" color={subtleText}>{fmtVolume(p.ytdVolume)}</Text>
                                  </Flex>
                                );
                              })}
                            </VStack>
                          </Box>
                        ) : (
                          <Text fontSize="sm" color={subtleText}>
                            No one has crossed {club10Label} yet — the club is wide open.
                          </Text>
                        )}
                        {chasing.length > 0 && (
                          <Box>
                            <Text fontSize="2xs" fontWeight="700" color={subtleText} textTransform="uppercase" letterSpacing="wide" mb={1.5}>
                              Closest to the door
                            </Text>
                            <VStack spacing={2.5} align="stretch">
                              {chasing.map((p) => {
                                const isMe = p.userId === viewAsUid;
                                return (
                                  <Box key={p.userId} bg={isMe ? meBg : undefined} borderRadius="md" p={isMe ? 2 : 0}>
                                    <Flex justify="space-between" align="center" mb={1}>
                                      <HStack spacing={1.5}>
                                        <Text fontSize="sm" fontWeight={isMe ? '700' : '500'}>
                                          {p.name}{isMe ? (impersonating ? ' (viewing)' : ' (you)') : ''}
                                        </Text>
                                        {p.onPace && <Badge colorScheme="green" fontSize="0.6rem">ON PACE</Badge>}
                                      </HStack>
                                      <Text fontSize="xs" color={subtleText}>{fmtVolume(p.volumeToGo)} to go</Text>
                                    </Flex>
                                    <Progress
                                      value={Math.min(p.pct, 100)}
                                      size="sm"
                                      borderRadius="full"
                                      colorScheme={p.onPace ? 'green' : 'blue'}
                                    />
                                  </Box>
                                );
                              })}
                            </VStack>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </VStack>
              )}
            </Box>

            {/* Private coaching review (admins see the viewed user's) */}
            <Box bg={reviewBg} border="1px solid" borderColor={reviewBorder} borderRadius="lg" p={4} data-tour="lb-review-card">
              <HStack justify="space-between" mb={1}>
                <HStack spacing={2}>
                  <Icon as={FiTrendingUp} color="purple.400" />
                  <Heading size="sm">{impersonating ? `${viewAsFirst}\u2019s Monthly Review` : 'My Monthly Review'}</Heading>
                </HStack>
                <Badge colorScheme="purple" variant="subtle" fontSize="0.6rem">
                  {impersonating ? 'ADMIN VIEW — PRIVATE TO THEM' : 'PRIVATE — ONLY YOU SEE THIS'}
                </Badge>
              </HStack>
              {reviewMonthLabel && (
                <HStack spacing={2} mb={2} align="baseline">
                  <Text fontSize="xs" color={subtleText}>
                    Grades <strong>{reviewMonthLabel}</strong> — the month that just ended, not the current one.
                  </Text>
                  {!reviewIsOnBoard && (
                    <Button size="xs" variant="link" colorScheme="purple" onClick={showReviewMonthOnBoard}>
                      Show {reviewMonthLabel.split(' ')[0]} rankings
                    </Button>
                  )}
                </HStack>
              )}
              {reviewLoading ? (
                <Skeleton h="90px" borderRadius="md" />
              ) : review ? (
                <VStack align="stretch" spacing={3}>
                  <Text fontSize="sm" whiteSpace="pre-wrap">{review.narrative}</Text>
                  {review.metrics?.own && (
                    <SimpleGrid columns={2} spacing={1.5} fontSize="xs">
                      <Text color={subtleText}>Funded</Text>
                      <Text fontWeight="600">
                        {review.metrics.own.fundedUnits ?? 0} · {fmtVolume(review.metrics.own.fundedVolume)}
                      </Text>
                      <Text color={subtleText}>Leads worked</Text>
                      <Text fontWeight="600">{review.metrics.own.totalLeads ?? 0}</Text>
                      {review.metrics.own.avgStlBiz !== null && review.metrics.own.avgStlBiz !== undefined && (
                        <>
                          <Text color={subtleText}>Speed-to-lead</Text>
                          <Text fontWeight="600">
                            {review.metrics.own.avgStlBiz}m
                            {review.metrics.team?.avgStlBiz?.p25 !== null && review.metrics.team?.avgStlBiz?.p25 !== undefined
                              ? ` (top quartile ${review.metrics.team.avgStlBiz.p25}m)` : ''}
                          </Text>
                        </>
                      )}
                      {review.metrics.own.conversationPct !== null && review.metrics.own.conversationPct !== undefined && (
                        <>
                          <Text color={subtleText}>Conversation rate</Text>
                          <Text fontWeight="600">
                            {review.metrics.own.conversationPct}%
                            {review.metrics.team?.conversationPct?.p75 !== null && review.metrics.team?.conversationPct?.p75 !== undefined
                              ? ` (top quartile ${review.metrics.team.conversationPct.p75}%)` : ''}
                          </Text>
                        </>
                      )}
                    </SimpleGrid>
                  )}
                  <Button size="xs" variant="outline" colorScheme="purple" leftIcon={<FiRefreshCw />}
                    onClick={() => loadReviewAs({ generate: true })} isLoading={reviewLoading}>
                    Regenerate
                  </Button>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" color={subtleText}>
                    {reviewError
                      ? reviewError
                      : `${impersonating ? `${viewAsFirst}\u2019s` : 'Your'} coaching review for last month hasn\u2019t been generated yet.`}
                  </Text>
                  <Button size="sm" colorScheme="purple" leftIcon={<FiZap />}
                    onClick={() => loadReviewAs({ generate: true })} isLoading={reviewLoading}>
                    {impersonating ? `Generate ${viewAsFirst}\u2019s review` : 'Generate my review'}
                  </Button>
                </VStack>
              )}
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* Admin: milestone targets + exclusions */}
      <Modal isOpen={adminModal.isOpen} onClose={adminModal.onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FiSettings} color="blue.400" />
              <Text>Leaderboard Admin</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* President's Club targets */}
            <Box data-tour="lb-admin-pc">
            <HStack spacing={2} mb={1}>
              <Icon as={FiStar} color="yellow.400" boxSize={4} />
              <Heading size="xs">President&apos;s Club</Heading>
            </HStack>
            <Text fontSize="xs" color={subtleText} mb={3}>
              Annual targets — loan officers qualify by hitting <strong>either</strong> one for the calendar year.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={5}>
              <FormControl>
                <FormLabel fontSize="sm">Funded volume target ($/year)</FormLabel>
                <NumberInput size="sm" value={milestoneDraft.pcVolume} min={1000000} step={1000000}
                  onChange={(_, v) => setMilestone('pcVolume', v)}>
                  <NumberInputField />
                  <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">Currently ${(Number(milestoneDraft.pcVolume) / 1e6).toFixed(1)}M</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Units target (loans/year)</FormLabel>
                <NumberInput size="sm" value={milestoneDraft.pcUnits} min={1} step={5}
                  onChange={(_, v) => setMilestone('pcUnits', v)}>
                  <NumberInputField />
                  <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">Funded units in the calendar year</FormHelperText>
              </FormControl>
            </SimpleGrid>
            </Box>

            <Divider mb={5} />

            {/* $10 Million Club */}
            <Box data-tour="lb-admin-club">
            <HStack spacing={2} mb={1}>
              <Icon as={FiDollarSign} color="green.400" boxSize={4} />
              <Heading size="xs">{club10Label} Club</Heading>
            </HStack>
            <Text fontSize="xs" color={subtleText} mb={3}>
              YTD funded-volume milestone. Crossing it earns the LO an additional comp bump.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={5}>
              <FormControl>
                <FormLabel fontSize="sm">Volume milestone ($ YTD)</FormLabel>
                <NumberInput size="sm" value={milestoneDraft.tmVolume} min={1000000} step={1000000}
                  onChange={(_, v) => setMilestone('tmVolume', v)}>
                  <NumberInputField />
                  <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">Currently ${(Number(milestoneDraft.tmVolume) / 1e6).toFixed(1)}M</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Comp bonus (bps)</FormLabel>
                <NumberInput size="sm" value={milestoneDraft.tmBps} min={1} max={100} step={1}
                  onChange={(_, v) => setMilestone('tmBps', v)}>
                  <NumberInputField />
                  <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
                <FormHelperText fontSize="xs">Extra basis points once they cross the milestone</FormHelperText>
              </FormControl>
            </SimpleGrid>
            </Box>

            <Divider mb={5} />

            {/* Exclusions */}
            <Box data-tour="lb-admin-exclusions">
            <HStack spacing={2} mb={1}>
              <Icon as={FiUserX} color="red.400" boxSize={4} />
              <Heading size="xs">Exclusions</Heading>
            </HStack>
            <Text fontSize="sm" color={subtleText} mb={4}>
              Excluded users never appear anywhere on this page — rankings, podium, weekly wins,
              President&apos;s Club, {club10Label} Club, or company totals — and their numbers stop skewing
              the team quartiles behind everyone&apos;s coaching reviews. Use this for leadership accounts
              that hold inherited past-client books (production they didn&apos;t originate).
            </Text>

            <FormControl mb={4}>
              <FormLabel fontSize="sm">Currently excluded</FormLabel>
              {exclusionDraft.length === 0 ? (
                <Text fontSize="sm" color={subtleText}>No one is excluded.</Text>
              ) : (
                <Wrap spacing={2}>
                  {exclusionDraft.map((userId) => (
                    <WrapItem key={userId}>
                      <Tag size="md" colorScheme="red" variant="subtle" borderRadius="full">
                        <TagLabel>{resolveUserName(userId)}</TagLabel>
                        <TagCloseButton
                          onClick={() => setExclusionDraft((prev) => prev.filter((u) => u !== userId))}
                        />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Add a user to exclude</FormLabel>
              <Select
                size="sm"
                placeholder="Select a user…"
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) setExclusionDraft((prev) => (prev.includes(value) ? prev : [...prev, value]));
                }}
              >
                {crmUsers
                  .filter((u) => !exclusionDraft.includes(u.uid))
                  .map((u) => (
                    <option key={u.uid} value={u.uid}>{u.name}</option>
                  ))}
              </Select>
              <FormHelperText fontSize="xs">
                Takes effect immediately for everyone after saving.
              </FormHelperText>
            </FormControl>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={adminModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={saveAdminSettings} isLoading={savingAdmin}>
              Save settings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default LeaderboardPage;
